import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Mail, Phone, Download, Trash2, Building2, MapPin } from 'lucide-react';
import { adminListEnquiries, adminUpdateEnquiry, adminDeleteEnquiry, asset } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const STATUS = ['new', 'contacted', 'closed'];

export default function AdminEnquiries() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);

  const load = () => adminListEnquiries().then((d) => { setItems(d); if (d.length && !active) setActive(d[0]); }).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const setStatus = async (id, status) => {
    try { await adminUpdateEnquiry(id, { status }); toast.success('Status updated'); load(); if (active?.id === id) setActive({ ...active, status }); }
    catch (e) { toast.error('Update failed'); }
  };
  const del = async (id) => {
    try { await adminDeleteEnquiry(id); toast.success('Deleted'); setActive(null); load(); }
    catch (e) { toast.error('Delete failed'); }
  };

  return (
    <div>
      <h1 className='font-head text-2xl font-semibold text-[#20272A]'>Enquiries</h1>
      <p className='text-sm text-[#20272A]/50'>{items.length} submission(s)</p>

      <div className='mt-6 grid gap-6 lg:grid-cols-12' data-testid='admin-enquiries-inbox'>
        <div className='lg:col-span-5'>
          <div className='divide-y divide-[#20272A]/8 overflow-hidden rounded-xl border border-[#20272A]/10 bg-white'>
            {items.map((e) => (
              <button key={e.id} onClick={() => setActive(e)} className={`flex w-full items-start justify-between gap-3 p-4 text-left transition-colors ${active?.id === e.id ? 'bg-[#075B61]/5' : 'hover:bg-[#F6F5F1]'}`}>
                <div className='min-w-0'>
                  <div className='truncate font-medium text-[#20272A]'>{e.name}</div>
                  <div className='truncate text-xs text-[#20272A]/50'>{e.requiredService || 'General'} · {e.projectLocation || '—'}</div>
                </div>
                <Badge variant={e.status === 'new' ? 'default' : 'secondary'} className={e.status === 'new' ? 'bg-[#B88A3B] text-[#20272A]' : ''}>{e.status}</Badge>
              </button>
            ))}
            {items.length === 0 && <div className='p-10 text-center text-sm text-[#20272A]/40'>No enquiries yet.</div>}
          </div>
        </div>

        <div className='lg:col-span-7'>
          {active ? (
            <div className='rounded-xl border border-[#20272A]/10 bg-white p-6'>
              <div className='flex items-start justify-between'>
                <div>
                  <h2 className='font-head text-xl font-semibold text-[#20272A]'>{active.name}</h2>
                  {active.company && <p className='text-sm text-[#20272A]/55'>{active.company}</p>}
                </div>
                <div className='flex items-center gap-2'>
                  <Select value={active.status} onValueChange={(v) => setStatus(active.id, v)}>
                    <SelectTrigger className='h-9 w-32'><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                  <button onClick={() => del(active.id)} className='inline-flex h-9 w-9 items-center justify-center rounded-lg border border-[#20272A]/10 text-red-600 hover:bg-red-50'><Trash2 size={15} /></button>
                </div>
              </div>

              <div className='mt-5 grid gap-4 sm:grid-cols-2'>
                <a href={`mailto:${active.email}`} className='flex items-center gap-2 text-sm text-[#20272A]/75 hover:text-[#075B61]'><Mail size={15} className='text-[#B88A3B]' /> {active.email}</a>
                {active.phone && <a href={`tel:${active.phone}`} className='flex items-center gap-2 text-sm text-[#20272A]/75 hover:text-[#075B61]'><Phone size={15} className='text-[#B88A3B]' /> {active.phone}</a>}
                {active.projectLocation && <span className='flex items-center gap-2 text-sm text-[#20272A]/75'><MapPin size={15} className='text-[#B88A3B]' /> {active.projectLocation}</span>}
                {active.requiredService && <span className='flex items-center gap-2 text-sm text-[#20272A]/75'><Building2 size={15} className='text-[#B88A3B]' /> {active.requiredService}</span>}
              </div>

              {active.timeline && <p className='mt-4 text-sm text-[#20272A]/60'><span className='text-[#20272A]/40'>Timeline:</span> {active.timeline}</p>}

              {active.projectDescription && (
                <div className='mt-5 rounded-lg bg-[#F6F5F1] p-4'>
                  <div className='text-xs uppercase tracking-wider text-[#20272A]/40'>Description</div>
                  <p className='mt-1 text-sm text-[#20272A]/80'>{active.projectDescription}</p>
                </div>
              )}

              {active.file_url && (
                <a href={asset(active.file_url)} target='_blank' rel='noreferrer' className='mt-5 inline-flex items-center gap-2 rounded-lg border border-[#075B61]/20 bg-[#075B61]/5 px-4 py-2 text-sm font-medium text-[#075B61] hover:bg-[#075B61]/10'>
                  <Download size={15} /> {active.file_name || 'Download attachment'}
                </a>
              )}

              <p className='mt-6 text-xs text-[#20272A]/35'>Received {new Date(active.createdAt).toLocaleString()}</p>
            </div>
          ) : (
            <div className='flex h-full min-h-[200px] items-center justify-center rounded-xl border border-dashed border-[#20272A]/15 text-[#20272A]/40'>Select an enquiry to view details</div>
          )}
        </div>
      </div>
    </div>
  );
}
